import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, CheckCircle2, Euro, CalendarClock, FileText, ShieldCheck } from "lucide-react";
import { api, fmtDate, fmtEur } from "@/lib/api";
import { PageHeader, StatCard, Panel, Empty, StatusBadge } from "@/components/common";
import BackupPanel from "@/pages/admin/BackupPanel";

const WINDOWS = [["31d", "31 giorni"], ["2m", "2 mesi"], ["3m", "3 mesi"]];

function WindowPicker({ value, onChange }) {
  return (
    <div className="inline-flex rounded-md border border-slate-700 bg-slate-900/60 p-0.5" role="tablist" data-testid="renewal-window-picker">
      {WINDOWS.map(([k, l]) => (
        <button key={k} role="tab" aria-selected={value === k} onClick={() => onChange(k)} data-testid={`renewal-window-${k}`}
          className={`px-3 py-1 text-xs rounded-sm transition-colors ${value === k ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-100"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const [s, setS] = useState(null);
  const [win, setWin] = useState(() => localStorage.getItem("marigot_renewal_window") || "31d");
  useEffect(() => {
    localStorage.setItem("marigot_renewal_window", win);
    api.get("/admin/stats", { params: { window: win } }).then((r) => setS(r.data));
  }, [win]);
  if (!s) return null;
  const label = WINDOWS.find(([k]) => k === win)?.[1];
  return (
    <div>
      <PageHeader eyebrow="Consolle amministrazione" title="Dashboard" desc="Situazione complessiva di clienti, abbonamenti e documenti." />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <StatCard label="Clienti" value={s.clients} icon={Users} testid="admin-stat-clients" />
        <StatCard label="Abb. attivi" value={s.active_subscriptions} icon={CheckCircle2} testid="admin-stat-active" delay={50} />
        <StatCard label="Ricavo mensile" value={fmtEur(s.mrr)} icon={Euro} testid="admin-stat-mrr" delay={100} />
        <StatCard label={`Rinnovi ${label}`} value={s.expiring} icon={CalendarClock} testid="admin-stat-expiring" delay={150} />
        <StatCard label="Documenti" value={s.documents} icon={FileText} testid="admin-stat-documents" delay={200} />
      </div>
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6">
        <Panel title={`Rinnovi da oggi al ${fmtDate(s.window_until)}`} testid="admin-upcoming" action={<WindowPicker value={win} onChange={setWin} />}>
          {s.upcoming.length ? (
            <ul className="divide-y divide-slate-800 max-h-[60vh] overflow-auto">
              {s.upcoming.map((u) => (
                <li key={u.id} className="px-5 py-3 flex items-center gap-4" data-testid={`upcoming-${u.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{u.service_name}</div>
                    <Link to={`/admin/clienti/${u.client_id}`} className="text-xs text-blue-400 hover:underline">{u.company_name}</Link>
                  </div>
                  <span className="text-xs text-slate-400 font-mono">{fmtDate(u.renewal_date)}</span>
                  <StatusBadge status={u.status} />
                </li>
              ))}
            </ul>
          ) : <Empty text={`Nessun rinnovo nei prossimi ${label}.`} />}
        </Panel>
        <Panel title="Amministratori del portale" testid="admin-accounts-list">
          <ul className="divide-y divide-slate-800">
            {s.admins.map((a) => (
              <li key={a.id} className="px-5 py-3 flex items-center gap-3">
                <ShieldCheck size={16} className="text-blue-400" />
                <div className="min-w-0">
                  <div className="text-sm truncate">{a.name}</div>
                  <div className="text-xs text-slate-500 truncate font-mono">{a.email}</div>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
      <div className="mt-6">
        <BackupPanel />
      </div>
    </div>
  );
}
